import { useTranslation } from 'react-i18next';
import { useChat } from '@ai-sdk/react';
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { 
  Send, 
  Sparkles, 
  Loader2, 
  Bot,
  User,
  Calendar,
  Clock,
  Plus,
  PalmtreeIcon,
  Building2,
  Mountain,
  Heart,
  Copy,
  Check
} from 'lucide-react';
import {
  Button,
  ScrollArea,
  cn,
  Textarea,
  useRepository,
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
  useLogin,
  isNativePlatform,
  getPlatform,
} from '@city-sage/aurum-avis-labs-components';
import { motion, AnimatePresence } from 'framer-motion';
import { QueryKeys } from '../../lib/QueryKeys';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ChatHistoryModal } from './ChatHistoryModal';
import { useAuth } from '@city-sage/aurum-avis-labs-components';
import { useIsAuthenticated } from '@city-sage/aurum-avis-labs-components';
import { toast } from 'sonner';
import { useToolCallHandling } from '../../lib/hooks/useToolCallHandling';
import { useDebounce } from '../../lib/hooks/useDebounce';
import { ToolCallIndicator } from './ToolCallIndicator';
import { CompactMapPreview } from './CompactMapPreview';
import { useMultipleDayStops } from '../../hooks/useMultipleDayStops';
import { useLegsByItinerary } from '../../hooks/useLegsByItinerary';
import { StopStateEnum } from '@city-sage/core-client';
import { MarkdownRenderer } from '../MarkdownRenderer';

interface ChatSheetProps {
  isOpen: boolean;
  onClose: () => void;
  initialMessage?: string;
  initialInput?: string;
}

export function ChatSheet({ 
  isOpen, 
  onClose, 
  initialInput,
}: ChatSheetProps) {
  const { t } = useTranslation();
  const routerState = useRouterState();
  
  // Extract IDs from URL as single source of truth
  const threadId = routerState.location.search.chatThreadId;
  const initialPrompt = routerState.location.search['initial-prompt'];
  const itineraryId = useMemo(() => {
    // Try both route patterns: /itineraries/ and /itinerary-canvas/
    const match = routerState.location.pathname.match(/\/(?:itineraries|itinerary-canvas)\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
    return match ? match[1] : undefined;
  }, [routerState.location.pathname]);

  // Create a session key that only changes when user explicitly starts new chat
  const [sessionId, setSessionId] = useState(() => Math.random().toString(36).substr(2, 9));
  
  // Function to force a new session (for New Chat button)
  const forceNewSession = useCallback(() => {
    setSessionId(Math.random().toString(36).substr(2, 9));
  }, []);

  return (
    <ChatSheetContent
      key={sessionId} // Force reset only when user starts new chat
      isOpen={isOpen}
      onClose={onClose}
      initialInput={initialInput || initialPrompt}
      threadId={threadId}
      itineraryId={itineraryId}
      onForceNewSession={forceNewSession}
    />
  );
}

interface ChatSheetContentProps {
  isOpen: boolean;
  onClose: () => void;
  initialInput?: string;
  threadId?: string;
  itineraryId?: string;
  onForceNewSession: () => void;
}

function ChatSheetContent({ 
  isOpen, 
  onClose, 
  initialInput,
  threadId,
  itineraryId,
  onForceNewSession,
}: ChatSheetContentProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [showExamples, setShowExamples] = useState(true);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isAuthError, setIsAuthError] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const routerState = useRouterState();
  
  // Login provider hook
  const { promptLoginModal } = useLogin();

  
  // Tool call handling
  const { 
    activeToolCalls, 
    currentToolCall, 
    handleToolCall, 
    clearToolCalls,
    createdItinerary
  } = useToolCallHandling();

  // Determine effective itineraryId (from URL or created itinerary)
  const effectiveItineraryId = useMemo(() => {
    return itineraryId || createdItinerary?.id;
  }, [itineraryId, createdItinerary?.id]);

  // Check if there are any itinerary-altering tool calls
  const hasItineraryAlteringToolCalls = useMemo(() => {
    const itineraryAlteringTypes = [
      'create_itinerary',
      'add_day',
      'add_stop',
      'add_leg',
      'update_itinerary',
      'update_stop',
      'optimize_route'
    ];
    return activeToolCalls.some(tc => itineraryAlteringTypes.includes(tc.type));
  }, [activeToolCalls]);

  // Determine if we should show the map
  const shouldShowMap = useMemo(() => {
    return !!effectiveItineraryId || hasItineraryAlteringToolCalls;
  }, [effectiveItineraryId, hasItineraryAlteringToolCalls]);

  // Fetch itinerary details for title and description
  const { data: itinerary } = useQuery({
    queryKey: QueryKeys.itinerary.details(effectiveItineraryId || ''),
    queryFn: () => api.findOneItinerary({ id: effectiveItineraryId! }),
    enabled: !!effectiveItineraryId,
  });

  // Fetch days for the itinerary
  const { data: days = [] } = useQuery({
    queryKey: QueryKeys.itinerary.days(effectiveItineraryId || ''),
    queryFn: () =>
      api.findAllItineraryDay({
        itineraryId: effectiveItineraryId!,
        sort: JSON.stringify(['number', 'ASC']),
        limit: 1000,
      }),
    enabled: !!effectiveItineraryId,
  });

  // Fetch stops for the itinerary
  const { data: stopsData } = useMultipleDayStops({
    itineraryId: effectiveItineraryId || '',
    days,
    states: [StopStateEnum.Accepted, StopStateEnum.Suggested],
  });

  const stops = stopsData?.allStops || [];

  // Fetch legs for the itinerary
  const { data: legs = [] } = useLegsByItinerary({
    itineraryId: effectiveItineraryId || '',
    enabled: !!effectiveItineraryId,
  });

  // Note: Thread ID updates are handled by the key pattern for clean resets

  const examplePrompts = useMemo(() => {
    if (itineraryId) {
      return [
        {
          icon: PalmtreeIcon,
          text: 'Add a beach day in between my current itinerary',
          category: 'Itinerary Enhancement',
        },
        {
          icon: Calendar,
          text: 'Adjust my schedule to be more realistic with travel times',
          category: 'Time Management',
        },
        {
          icon: Building2,
          text: 'Add some local restaurants and food experiences',
          category: 'Dining & Culture',
        }
      ];
    }

    return [
      {
        icon: Building2,
        text: 'common.chat.examples.trip1',
        category: 'common.chat.categories.cityBreak',
      },
      {
        icon: Mountain,
        text: 'common.chat.examples.trip2',
        category: 'common.chat.categories.adventure',
      },
      {
        icon: Heart,
        text: 'common.chat.examples.trip3',
        category: 'common.chat.categories.romance',
      },
    ];
  }, [itineraryId]);

  // Handle thread ID updates from chat responses
  const handleThreadIdUpdate = useCallback((newThreadId: string | undefined) => {
    const currentSearch = routerState.location.search as Record<string, any>;
    const newSearch = { ...currentSearch };
    
    if (newThreadId) {
      newSearch.chatThreadId = newThreadId;
    } else {
      delete newSearch.chatThreadId;
    }
    
    navigate({
      to: window.location.pathname,
      search: newSearch,
      replace: true,
      resetScroll: false
    });
  }, [navigate, routerState.location.search]);

  // Auth hooks
  const { getToken } = useAuth();
  const isAuthenticated = useIsAuthenticated();
  const { api } = useRepository();

  // Token state for auth
  const [token, setToken] = useState<string | null>(null);

  // Fetch account data for credits (only if authenticated)
  const { data: account } = useQuery({
    queryKey: QueryKeys.account.details(),
    queryFn: () => api.getMyAccount(),
    enabled: !!isAuthenticated,
    staleTime: 1000 * 60, // 1 minute
  });

  const credits = account?.credits ?? null;

  const { data: initialMessages } = useQuery({
    queryKey: QueryKeys.chat.initialMessages(threadId, itineraryId),
    queryFn: async () => {
      // If threadId is 'new', start with empty messages
      if (threadId === 'new') {
        return [];
      }

      if (threadId) {
        return api.getChatHistory({ threadId });
      }

      if (itineraryId && account?.id) {
        const newestChat = await api.findAllAccountChat({ 
          accountId: account.id, 
          filter: JSON.stringify({ itineraryId }),
          sort:JSON.stringify(["createdAt", "DESC"]),
          limit: 1
        });
        if (newestChat[0]) {
          return api.getChatHistory({ threadId: newestChat[0].threadId });
        }
      }
      
      return [];
    },
    select: (data) => {
      // Aggregate consecutive assistant messages
      return data.reduce((acc: any[], message, index) => {
        if (index > 0 && 
            message.role === 'assistant' && 
            acc.length > 0 && 
            acc[acc.length - 1].role === 'assistant') {
          acc[acc.length - 1].content += '\n' + message.content;
        } else {
          acc.push({...message});
        }
        return acc;
      }, []);
    },
    enabled: (!!threadId || (!!itineraryId && !!account?.id)) && !!isAuthenticated,
  });

  // Fetch token when authentication changes
  useEffect(() => {
    const fetchToken = async () => {
      if (!isAuthenticated) {
        setToken(null);
        return;
      }
      try {
        const newToken = await getToken();
        setToken(newToken);
      } catch (error) {
        console.error('Failed to get token:', error);
        setToken(null);
      }
    };

    fetchToken();
  }, [isAuthenticated, getToken]);

  // Helper functions
  const parseApiError = (error: any) => {
    if (error?.message) return { message: error.message };
    if (typeof error === 'string') return { message: error };
    return { message: 'An unexpected error occurred' };
  };

  const isUnauthorizedError = (error: any) => {
    return error?.status === 401 || error?.message?.includes('401') || error?.message?.includes('Unauthorized');
  };

  const getPlaceholderText = () => {
    if (credits === 0 && isAuthenticated) return t('common.chat.placeholderCredits');
    return t('common.chat.placeholder');
  };

  const copyToClipboard = async (text: string, messageIndex: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedMessageId(`message-${messageIndex}`);
      toast.success(t('common.chat.copied'));
      
      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setCopiedMessageId(null);
      }, 2000);
    } catch (error) {
      console.error('Failed to copy text:', error);
      toast.error(t('common.chat.copyError'));
    }
  };

  const queryClient = useQueryClient();
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Initialize input from URL search params or initialInput
  const getInitialInputValue = () => {
    const urlInputParam = routerState.location.search['chat-input'];
    const urlInitialPrompt = routerState.location.search['initial-prompt'];
    
    // Priority: URL chat-input > initialInput prop > URL initial-prompt
    return urlInputParam || initialInput || urlInitialPrompt || '';
  };

  // Construct API endpoint with proper base URL for Capacitor
  const apiEndpoint = useMemo(() => {
    // Determine base path based on environment
    const isCapacitor = isNativePlatform();
    let basePath: string;
    
    if (isCapacitor) {
      // For Capacitor mobile apps, use environment variable or fallback to localhost
      basePath = import.meta.env.VITE_API_BASE_URL || "http://localhost:3000/api";
      console.debug('[CHAT] Capacitor platform detected:', {
        platform: getPlatform(),
        basePath
      });
    } else {
      // For web browser, use relative path (works with Vite proxy)
      basePath = '/api';
    }
    
    // Construct chat endpoint
    let endpoint = `${basePath}/chat`;
    
    // Don't include 'new' threadId in the URL path
    if (threadId && threadId !== 'new') {
      endpoint += `/${threadId}`;
    }
    
    // Add itineraryId as query parameter
    if (itineraryId) {
      endpoint += `?itineraryId=${itineraryId}`;
    }
    
    console.debug('[CHAT] Using API endpoint:', endpoint);
    return endpoint;
  }, [threadId, itineraryId]);

  const {
    messages,
    input,
    handleInputChange,
    handleSubmit,
    status,
    error,
    setInput,
    stop,
  } = useChat({
    api: apiEndpoint,
    initialInput: getInitialInputValue(),
    initialMessages: initialMessages,
    experimental_prepareRequestBody: ({ messages: allMessages }: { messages: any[] }) => {
      const latestMessage = allMessages[allMessages.length - 1];
      return {
        messages: latestMessage ? [latestMessage] : [],
        threadId: threadId === 'new' ? undefined : threadId,
      };
    },
    headers: {
      ...(token && isAuthenticated ? { Authorization: `Bearer ${token}` } : {}),
    },
    onToolCall: handleToolCall,
    onResponse(response) {
      const responseThreadId = response.headers.get('x-thread-id');
      const currentSearch = routerState.location.search as Record<string, any>;
      const newSearch = { ...currentSearch };
      
      // Update thread ID if new one received
      if (responseThreadId && responseThreadId !== threadId) {
        newSearch.chatThreadId = responseThreadId;
      }
      
      // Clear URL input params after successful response
      if (newSearch['initial-prompt']) {
        delete newSearch['initial-prompt'];
      }
      if (newSearch['chat-input']) {
        delete newSearch['chat-input'];
      }
      
      // Only navigate if something actually changed
      const currentSearchStr = JSON.stringify(routerState.location.search);
      const newSearchStr = JSON.stringify(newSearch);
      if (currentSearchStr !== newSearchStr) {
        navigate({
          to: window.location.pathname,
          search: newSearch,
          replace: true,
          resetScroll: false
        });
      }
      
      scrollToBottom();
      setShowExamples(false);

      if (isAuthError) {
        setIsAuthError(false);
      }
    },
    onFinish: () => {
      scrollToBottom();
    
      // Use current itineraryId from URL for cache invalidation
      const currentItineraryMatch = window.location.pathname.match(/^\/itineraries\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
      const currentItineraryId = currentItineraryMatch ? currentItineraryMatch[1] : undefined;
      
      if (currentItineraryId) {
        queryClient.invalidateQueries({
          predicate: (query) => JSON.stringify(query.queryKey).includes(currentItineraryId)
        });
      }

      // Navigation now happens immediately when itinerary creation is complete
      // No need to wait for stream to finish
    },
    onError(error) {
      const parsedError = parseApiError(error);
      queryClient.invalidateQueries({ queryKey: QueryKeys.account.details() });

      if (isUnauthorizedError(error)) {
        setIsAuthError(true);
        
        promptLoginModal({
          title: t('common.auth.title'),
          description: t('common.auth.description'),
        });
      } else {
        console.error('Chat error:', parsedError);
        toast.error(parsedError.message || t('common.chat.error'));
      }
    },
  });

  // Debounce input for URL syncing
  const debouncedInput = useDebounce(input, 500);

  // Sync debounced input to URL search params for input persistence
  useEffect(() => {
    // Only sync if chat is open and there's meaningful input
    if (!isOpen || !debouncedInput.trim()) {
      return;
    }

    // Don't sync during active chat streaming to avoid interference
    if (status === 'streaming' || status === 'submitted') {
      return;
    }

    // Don't sync if there are already messages (input is temporary)
    if (messages.length > 0) {
      return;
    }

    const currentSearch = routerState.location.search as Record<string, any>;
    const newSearch = { ...currentSearch };
    
    // Update or remove chat-input param
    if (debouncedInput.trim()) {
      newSearch['chat-input'] = debouncedInput.trim();
    } else if (newSearch['chat-input']) {
      delete newSearch['chat-input'];
    }

    // Only navigate if the param actually changed
    if (newSearch['chat-input'] !== currentSearch['chat-input']) {
      navigate({
        to: window.location.pathname,
        search: newSearch,
        replace: true,
        resetScroll: false
      });
    }
  }, [debouncedInput, isOpen, status, messages.length, navigate, routerState.location.search]);

  // Clear URL input param when chat is closed or reset
  useEffect(() => {
    if (!isOpen) {
      const currentSearch = routerState.location.search as Record<string, any>;
      if (currentSearch['chat-input']) {
        const newSearch = { ...currentSearch };
        delete newSearch['chat-input'];
        navigate({
          to: window.location.pathname,
          search: newSearch,
          replace: true,
          resetScroll: false
        });
      }
    }
  }, [isOpen, navigate, routerState.location.search]);

  // Note: Chat reset is now handled automatically by the key prop pattern in the parent component

  const handleExampleClick = (example: string) => {
    setInput(example);
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  const handleNewChat = () => {
    stop();
    
    // Clear active tool calls
    clearToolCalls();
    
    // Then force a new session which will reset all useChat state
    onForceNewSession();
    
    // Navigate with threadId set to 'new' for fresh chat
    const currentSearch = routerState.location.search as Record<string, any>;
    navigate({
      to: window.location.pathname,
      search: { 
        ...currentSearch,
        chatThreadId: 'new'
      },
      replace: true,
      resetScroll: false
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (input.trim() && status !== 'streaming') {
        handleSubmit(e as any);
      }
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Tool call handling is now done via onToolCall callback in useChat

  return (
    <>
      <Drawer repositionInputs={false} open={isOpen} onOpenChange={onClose}>
        <DrawerContent className="h-[98dvh]">
          {/* Fixed Header */}
          <DrawerHeader className="px-4 md:px-6 py-4 md:py-5 border-b border-border flex-shrink-0 bg-background">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="h-10 w-10 md:h-12 md:w-12 flex items-center justify-center flex-shrink-0 p-1.5">
                  <img
                    src={window.location.origin + '/logo_only.webp'}
                    alt="CitySage Logo"
                    className="h-full w-full object-contain"
                  />
                </div>
                <div className="min-w-0">
                  <DrawerTitle className="text-left text-lg md:text-xl font-semibold leading-tight text-foreground">
                    {t('common.chat.title')}
                  </DrawerTitle>
                  <p className="text-xs md:text-sm text-muted-foreground leading-tight mt-0.5">
                    {t('common.chat.subtitle')}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  onClick={handleNewChat}
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 md:h-10 md:w-10 rounded-full hover:bg-accent transition-colors"
                  aria-label={t('common.chat.newChat')}
                >
                  <Plus className="h-4 w-4 md:h-5 md:w-5" />
                </Button>
                <Button
                  onClick={() => setIsHistoryOpen(true)}
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 md:h-10 md:w-10 rounded-full hover:bg-accent transition-colors"
                  aria-label={t('common.chat.history.title')}
                >
                  <Clock className="h-4 w-4 md:h-5 md:w-5" />
                </Button>
              </div>
            </div>
          </DrawerHeader>

          {/* Scrollable Content Area */}
          <ScrollArea className="flex-1">
            <div className="p-4 md:p-6 space-y-4 md:space-y-6">
                {messages.length === 0 && showExamples && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-8 md:space-y-10"
                  >
                    <div className="space-y-3 md:space-y-4">
                      <div className="mb-4 md:mb-6">
                        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
                          {t('common.chat.tryAskingAbout')}
                        </p>
                        <div className="h-px w-12 bg-accent" />
                      </div>
                      {examplePrompts.map((example, index) => {
                        const Icon = example.icon;
                        return (
                          <motion.button
                            key={index}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.1 }}
                            onClick={() => handleExampleClick(t(example.text))}
                            className="w-full p-4 md:p-5 text-left rounded-lg border border-border bg-card hover:bg-accent hover:border-primary/50 transition-all group"
                          >
                            <div className="flex items-center gap-3 md:gap-4">
                              <div className="h-10 w-10 md:h-12 md:w-12 rounded-full bg-primary/10 group-hover:bg-primary/20 flex items-center justify-center transition-colors flex-shrink-0">
                                <Icon className="h-5 w-5 md:h-6 md:w-6 text-primary" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-foreground text-sm md:text-base mb-1 leading-tight">{t(example.text)}</p>
                                <span className="text-xs md:text-sm text-primary font-medium">{t(example.category)}</span>
                              </div>
                            </div>
                          </motion.button>
                        );
                      })}
                    </div>
                  </motion.div>
                )}

                <AnimatePresence>
                  {messages.map((message, index) => {
                    const isUser = message.role === 'user';
                    const isLastMessage = index === messages.length - 1;
                    const isEmptyAssistant = !isUser && message.content.trim() === '';
                    
                    // Don't render empty assistant messages - map shows progress instead
                    if (isEmptyAssistant) {
                      return null;
                    }
                    
                    return (
                      <div key={index}>
                        {/* Show compact map above the agent message if it's the last message */}
                        {!isUser && isLastMessage && shouldShowMap && (
                          <div className="mb-4 md:mb-6">
                            <CompactMapPreview
                              stops={stops}
                              days={days}
                              legs={legs}
                              itineraryId={effectiveItineraryId}
                              title={itinerary?.title || createdItinerary?.title}
                              shortDescription={itinerary?.shortDescription || createdItinerary?.shortDescription}
                              toolCall={
                                activeToolCalls.length > 0 && (status === 'streaming' || status === 'submitted')
                                  ? activeToolCalls[activeToolCalls.length - 1]
                                  : null
                              }
                              onCloseChat={onClose}
                            />
                          </div>
                        )}

                        <motion.div
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={cn(
                            'flex gap-3 md:gap-4 group',
                            isUser ? 'justify-end' : 'justify-start'
                          )}
                        >
                          {!isUser && (
                            <div className="h-9 w-9 md:h-10 md:w-10 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center flex-shrink-0 shadow-md">
                              <Bot className="h-4 w-4 md:h-5 md:w-5 text-white" />
                            </div>
                          )}
                          <div
                            className={cn(
                              'max-w-[85%] md:max-w-[75%] rounded-xl md:rounded-2xl px-4 py-3 md:px-5 md:py-4 text-sm md:text-base relative group shadow-sm',
                              isUser
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-card text-foreground border border-border'
                            )}
                          >
                            {isUser ? (
                              <div className="whitespace-pre-wrap break-words leading-relaxed pr-8 md:pr-10">
                                {message.content}
                              </div>
                            ) : (
                              <div className="pr-8 md:pr-10">
                                <MarkdownRenderer
                                  content={message.content}
                                  className="text-sm md:text-base leading-relaxed prose-headings:mt-2 prose-headings:mb-1 prose-p:my-1 prose-ul:my-1 prose-li:my-0.5 prose-headings:text-foreground prose-p:text-foreground"
                                />
                              </div>
                            )}
                            
                            {/* Copy button - integrated into message bubble */}
                            {message.content.trim() && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className={cn(
                                  "absolute top-2 right-2 h-7 w-7 md:h-8 md:w-8 rounded-full opacity-60 hover:opacity-100 transition-opacity",
                                  "hover:bg-black/10 dark:hover:bg-white/10",
                                  isUser 
                                    ? "text-primary-foreground/70 hover:text-primary-foreground" 
                                    : "text-muted-foreground hover:text-foreground"
                                )}
                                onClick={() => copyToClipboard(message.content, index)}
                                aria-label={t('common.chat.copyMessage')}
                              >
                                {copiedMessageId === `message-${index}` ? (
                                  <Check className="h-3.5 w-3.5 md:h-4 md:w-4 text-green-600" />
                                ) : (
                                  <Copy className="h-3.5 w-3.5 md:h-4 md:w-4" />
                                )}
                              </Button>
                            )}
                          </div>
                        </motion.div>
                      </div>
                    );
                  })}
                </AnimatePresence>

                {status === 'submitted' && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex gap-3 md:gap-4"
                  >
                    <div className="h-9 w-9 md:h-10 md:w-10 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center flex-shrink-0 shadow-md">
                      <Bot className="h-4 w-4 md:h-5 md:w-5 text-white" />
                    </div>
                    <div className="bg-card border border-border rounded-xl md:rounded-2xl px-4 md:px-5 py-3 md:py-4 shadow-sm">
                      <div className="flex items-center gap-1.5">
                        <motion.div
                          className="w-2 h-2 md:w-2.5 md:h-2.5 bg-primary rounded-full"
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ duration: 1, repeat: Infinity, delay: 0 }}
                        />
                        <motion.div
                          className="w-2 h-2 md:w-2.5 md:h-2.5 bg-primary rounded-full"
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
                        />
                        <motion.div
                          className="w-2 h-2 md:w-2.5 md:h-2.5 bg-primary rounded-full"
                          animate={{ scale: [1, 1.2, 1] }}
                          transition={{ duration: 1, repeat: Infinity, delay: 0.4 }}
                        />
                      </div>
                    </div>
                  </motion.div>
                )}

                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 md:p-5 rounded-xl md:rounded-2xl bg-destructive/10 border border-destructive/20"
                  >
                    <p className="text-sm md:text-base text-destructive leading-relaxed">
                      {t('common.chat.error')}
                    </p>
                  </motion.div>
                )}


                <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          {/* Fixed Input Section */}
          <DrawerFooter className="p-4 md:p-6 border-t border-border bg-background">
            <form onSubmit={handleSubmit} className="relative w-full">
              <div className="relative">
                <Textarea
                  ref={textareaRef}
                  value={input}
                  autoFocus={true}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  placeholder={getPlaceholderText()}
                  className="min-h-[52px] md:min-h-[60px] max-h-[120px] md:max-h-[140px] rounded-xl md:rounded-2xl resize-none pr-12 md:pr-14 py-3 md:py-4 text-sm md:text-base transition-all border-border"
                  disabled={status === 'streaming' || (credits === 0 && isAuthenticated)}
                />
                
                <Button
                  type="submit"
                  disabled={!input.trim() || status === 'streaming' || status === 'submitted' || (credits === 0 && isAuthenticated)}
                  className="absolute right-2 md:right-3 bottom-2 md:bottom-3 h-8 w-8 md:h-9 md:w-9 rounded-full p-0 flex items-center justify-center shadow-md z-10"
                  aria-label={t('common.chat.send')}
                >
                  {(status === 'streaming' || status === 'submitted') ? (
                    <Loader2 className="h-4 w-4 md:h-5 md:w-5 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 md:h-5 md:w-5" />
                  )}
                </Button>
              </div>
            </form>

            {/* Credits warning */}
            {credits === 0 && isAuthenticated && (
              <div className="mt-3 md:mt-4 p-3 md:p-4 bg-destructive/10 border border-destructive/20 rounded-xl">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm md:text-base text-destructive">
                    {t('common.chat.creditsRequired')}
                  </span>
                  <Link to="/profile">  
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 md:h-9 text-xs md:text-sm text-destructive hover:text-destructive/80 hover:bg-destructive/10"
                  >
                    {t('common.chat.getCredits')}
                  </Button>
                  </Link>
                </div>
              </div>
            )}
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Chat History Modal */}
      <ChatHistoryModal
        isOpen={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
        onSelectThread={(selectedThreadId) => {
          handleThreadIdUpdate(selectedThreadId);
          setIsHistoryOpen(false);
        }}
      />

    </>
  );
} 